import { useState } from "react";
import {
    ArrowRight,
    Boxes,
    Building2,
    Check,
    ChevronRight,
    Clock3,
    Download,
    Gift,
    Home,
    Menu,
    Phone,
    Search,
    ShieldCheck,
    Sparkles,
    Truck,
    Users,
    X,
} from "lucide-react";
import "./RoomBuddyHome.css";
import NewWhatsAppButton from "../components/NewWhatsAppButton";


import heroBuddy from "../assets/promo_human2-Photoroom.webp";
import team from "../assets/helpers002.webp";
import movingInside from "../assets/helpers-service_1.webp";
import shiftingPromo from "../assets/promo15.webp";
import referralPromo from "../assets/referEarn01.webp";
import ownerSupport from "../assets/watchmenshire.webp";
import movingTruck from "../assets/helpers-service22.webp";
import cleaning from "../assets/home_cleaning_service.webp";
import roomBuddyLogo from "../assets/roombuddy-logo-blue.png";
import ScannerRoomBuddy from "../assets/ScannerRoomBuddy.png";
import twobhkflat from "../assets/2bhk.png";
import buddyimg from "../assets/buddy.png";
import buddyimgtwo from "../assets/buddy2.png";


const navItems = [
    ["Home", "#home"],
    ["Instant Assistant", "#instant-assistant"],
    ["Room Search", "#room-search"],
    ["Shifting", "#shifting"],
    ["Refer & Earn", "#refer"],
    ["For Owners", "#owners"],
];

const services = [
    { icon: Clock3, image: team, title: "Instant Assistant", copy: "Choose a property-search window from 2 hours to a full day.", href: "#instant-assistant" },
    { icon: Search, image: ownerSupport, title: "₹499 Room Search Slot", copy: "Get one month of dedicated property-search support.", href: "#room-search" },
    { icon: Truck, image: movingInside, title: "Shifting Services", copy: "Vehicles, cartons, moving, cleaning and organising support.", href: "#shifting" },
    { icon: Gift, image: referralPromo, title: "Refer & Earn", copy: "Earn ₹999 after your referred customer completes shifting.", href: "#refer" },
    { icon: Building2, image: buddyimg, title: "Free Property Listing", copy: "Owners can list available homes with RoomBuddy for free.", href: "#owners" },
];

const hours = ["2 Hours", "3 Hours", "4 Hours", "5 Hours", "6 Hours", "Full Day"];

const roomSteps = [
    ["01", "Download the App", "Install RoomBuddy on your Android phone."],
    ["02", "Choose Your Service", "Pick Instant Assistant or the ₹499 Room Search Slot."],
    ["03", "Share Requirements", "Add preferred locations and home requirements."],
    ["04", "Explore Properties", "Receive available property details in the app."],
    ["05", "Contact Owners", "Use the available details to contact owners directly."],
    ["06", "Finalize Your Home", "Choose the property that works for you."],
    ["07", "Complete Your Move", "Book RoomBuddy shifting support in the app."],
];

const prices = [
    ["1RK", "₹2,499"],
    ["1BHK", "₹3,499"],
    ["2BHK", "₹4,599"],
    ["3BHK", "₹6,000"],
];

const shiftingFeatures = [
    [Truck, "Low-Cost Vehicles", "Choose an affordable vehicle for your move."],
    [Boxes, "Unlimited Packing Cartons", "Get the cartons needed for your shifting requirement."],
    [Users, "Moving Support", "Get support for loading and unloading."],
    [Sparkles, "Cleaning Support", "Two cleaning women help clean after shifting."],
    [Home, "Organising Support", "Get help organising your new home after moving."],
];

const benefits = [
    [Home, "Everything in One App", "Find rental homes and book shifting services in one place."],
    [Clock3, "Flexible Property Search", "Choose Instant Assistant from 2 hours to a full day."],
    [Phone, "Direct Owner Contact", "See available owner details and contact them directly."],
    [ShieldCheck, "Affordable Services", "Practical support with clear, easy-to-understand charges."],
    [Truck, "Complete Moving Support", "Get help from packing through cleaning and organising."],
    [Gift, "Earn by Referring", "Earn ₹999 after a successful referred shifting."],
];

const DownloadButton = ({ compact = false, dark = false }) => (
    <a
        className={`button button-primary${compact ? " button-compact" : ""}${dark ? " button-dark" : ""}`}
        href="https://play.google.com/store/apps/details?id=com.roombuddy.app"
        target="_blank"
        rel="noopener noreferrer"
    >
        <Download aria-hidden="true" />
        Download App
    </a>
);

function Brand() {
    return (
        <a className="brand" href="#home" aria-label="RoomBuddy home">
            <img className="brand-logo" src={roomBuddyLogo} alt="" aria-hidden="true" />
            <span><strong style={{fontSize:"1rem"}}>RoomBuddy</strong><small style={{fontSize:"0.65rem"}}>Your Rental Partner</small></span>
        </a>
    );
}

export default function RoomBuddyHome() {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <main className="site-shell" id="home">
            <header className="site-header">
                <nav className="nav-shell" aria-label="Main navigation">
                    <Brand />
                    <div className="desktop-nav">
                        {navItems.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
                    </div>
                    <div className="nav-actions">
                        <DownloadButton compact />
                        <button className="menu-button" type="button" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
                            {menuOpen ? <X /> : <Menu />}
                        </button>
                    </div>
                </nav>
                {menuOpen && (
                    <div className="mobile-menu">
                        {navItems.map(([label, href]) => <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}<ChevronRight /></a>)}
                        <DownloadButton />
                    </div>
                )}
            </header>

            <section className="hero section-pad">
                <div className="hero-copy">
                    <span className="eyebrow"><Sparkles /> One app for your complete rental journey</span>
                    <h1>Find Move <span>Live Better</span></h1>
                    <p className="lead">Find your next home, discover properties and make your move easier — all through the RoomBuddy app.</p>
                    <div className="button-row">
                        <DownloadButton />
                        <a className="button button-secondary" href="#services">Explore Services <ArrowRight /></a>
                    </div>
                    <div className="hero-trust">
                        <span><Check /> App-based services</span>
                        <span><Check /> Direct owner contact</span>
                        <span><Check /> Moving support</span>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="hero-photo">
                        <img src={heroBuddy} alt="RoomBuddy employee welcoming customers" />
                    </div>
                    <div className="floating-card floating-search">
                        <Search />
                        <span><small>Room search From</small><strong style={{ fontWeight: "bold" }}>₹369</strong></span>
                    </div>
                    <div className="floating-card floating-shift">
                        <Search />
                        <span><small>One month room search</small><strong style={{ fontWeight: "bold" }}>₹499</strong></span>
                    </div>
                </div>
            </section>

            <section className="section-pad section-heading" id="services">
                <span className="section-kicker">RoomBuddy services</span>
                <h2>Everything You Need for Your Rental Journey</h2>
                <p>From finding a home to moving in, RoomBuddy brings rental and shifting services together in one app.</p>
                <div className="service-grid">
                    {services.map(({ icon: Icon, image, title, copy, href }, index) => (
                        <a className={`service-card glass-panel service-${index + 1}`} href={href} key={title}>
                            <img className="service-card-image" src={image} alt="" />
                            <span className="service-card-shade" />
                            <span className="service-card-content"><span className="icon-tile"><Icon /></span><h3>{title}</h3><p>{copy}</p><span className="card-link">Explore <ArrowRight /></span></span>
                        </a>
                    ))}
                </div>
            </section>

            <section className="content-band" id="instant-assistant">
                <div className="section-pad two-column">
                    <div className="content-copy">
                        <span className="section-kicker">Instant Assistant</span>
                        <h2>Search for Homes by the Hours You Need</h2>
                        <p>Need a quick property search? Choose the amount of time you want and let RoomBuddy search available properties for you.</p>
                        <div className="hour-grid">
                            {hours.map((hour, index) => <div className={index === 0 ? "hour active" : "hour"} key={hour}><Clock3 />{hour}</div>)}
                        </div>
                        <a className="button button-primary" href="#download">Book Instant Assistant <ArrowRight /></a>
                        <small className="note">Instant Assistant services are available through the RoomBuddy mobile app.</small>
                    </div>
                    <div className="image-frame glass-panel"><img src={team} alt="RoomBuddy Instant Assistant team" /></div>
                </div>
                <div className="section-pad process-grid four-steps" style={{ paddingTop: 0 }}>
                    {[
                        ["01", "Choose Your Hours", "Select the time you need for your property search."],
                        ["02", "RoomBuddy Searches", "Our Buddy searches by your locations and requirements."],
                        ["03", "View Property Details", "See photos, location, details and owner contact."],
                        ["04", "Contact Owners Directly", "Contact the owner using the available details."],
                    ].map(([n, title, copy]) => <article key={n}><span>{n}</span><h3>{title}</h3><p>{copy}</p></article>)}
                </div>
            </section>

            <section className="section-pad property-showcase" style={{ paddingTop: 0 }}>
                <div className="section-heading compact-heading">
                    <span className="section-kicker">After your search</span>
                    <h2>Get the Details You Need</h2>
                    <p>Property details available after your search.</p>
                </div>
                <div className="property-layout">
                    <div className="property-image"><img src={twobhkflat} alt="RoomBuddy team member inside a listed home" /></div>
                    <div className="property-card glass-solid">
                        <div className="property-card-top"><span className="status-dot" /> Available property</div>
                        <h3>2BHK · HSR Layout</h3>
                        <p>Kondapur · Furnished</p>
                        <div className="property-meta"><strong>₹32,000</strong><span>Monthly rent</span></div>
                        <div className="details-list"><span><Check /> Property photos</span><span><Check /> Full details</span><span><Check /> Owner contact</span></div>
                        <a className="button button-primary" href="#download"><Phone /> View Contact</a>
                    </div>
                </div>
            </section>

            <section className="search-offer" id="room-search">
                <div className="section-pad offer-layout">
                    <div className="price-orbit"><span style={{ fontWeight: "bold", fontSize: "2rem" }}>₹499</span><small>one slot</small></div>
                    <div className="content-copy">
                        <span className="section-kicker section-kicker-light">Monthly room search</span>
                        <h2>One Slot. One Month of Room Search.</h2>
                        <p>Book a ₹499 Room Search Slot and get RoomBuddy&apos;s property search support for one month.</p>
                        <ul className="check-list"><li><Check /> Multiple property options</li><li><Check /> Review complete property details</li><li><Check /> Shortlist and finalize your new home</li></ul>
                        <a className="button button-light" href="#download">Book ₹499 Slot <ArrowRight /></a>
                    </div>
                </div>
            </section>

            <section className="section-pad pricing-section" style={{ paddingTop: 0 }}>
                <div className="section-heading compact-heading">
                    <span className="section-kicker">RoomBuddy service charge</span>
                    <h2>Simple Charges After You Finalize Your Home</h2>
                    <p>After you finalize through RoomBuddy room search, the service charge is based on the home type. This is not monthly rent.</p>
                </div>
                <div className="pricing-grid">
                    {prices.map(([type, price]) => <article className="price-card glass-panel" key={type}><small>Home type</small><h3>{type}</h3><span>{price}</span><p>One-time service charge</p></article>)}
                </div>
                <div className="center-action"><a className="button button-primary" href="#download">Get Started <ArrowRight /></a></div>
            </section>

            <section className="content-band timeline-section">
                <div className="section-pad" style={{ paddingTop: 0 }}>
                    <div className="section-heading compact-heading"><span className="section-kicker">How it works</span><h2>From Search to Settled</h2></div>
                    <div className="timeline">
                        {roomSteps.map(([n, title, copy]) => <article key={n}><span>{n}</span><div><h3>{title}</h3><p>{copy}</p></div></article>)}
                    </div>
                </div>
            </section>

            <section className="section-pad shifting-section" id="shifting">
                <div className="two-column">
                    <div className="image-frame moving-image"><img src={movingTruck} alt="RoomBuddy shifting team unloading boxes" /></div>
                    <div className="content-copy">
                        <span className="section-kicker">Moving into your new home?</span>
                        <h2>Book Your Shifting Service in the RoomBuddy App</h2>
                        <p>Once you&apos;ve found your new home, RoomBuddy can help you move with affordable shifting support.</p>
                        <div className="feature-list">
                            {shiftingFeatures.map(([Icon, title, copy]) => <div key={title}><span className="icon-tile"><Icon /></span><span><strong>{title}</strong><small>{copy}</small></span></div>)}
                        </div>
                    </div>
                </div>
                <div className="package-card glass-solid">
                    <div><span className="section-kicker">Complete moving package</span><h2>Complete Your Move With RoomBuddy</h2></div>
                    <div className="package-items">{["Vehicle", "Packing cartons", "Loading support", "Unloading support", "Cleaning support", "Organising support"].map((item) => <span key={item}><Check />{item}</span>)}</div>
                    <a className="button button-primary" href="#download">Book Shifting <ArrowRight /></a>
                    <small>Availability and pricing are provided through the RoomBuddy app based on your requirements.</small>
                </div>
            </section>

            <section className="referral-section" id="refer">
                <div className="section-pad referral-layout">
                    <div className="referral-image"><img src={referralPromo} alt="RoomBuddy Refer and Earn promotion" /></div>
                    <div className="content-copy">
                        <span className="section-kicker section-kicker-light">Unlimited referrals available</span>
                        <h2>Refer a Friend. Earn <em>₹999.</em></h2>
                        <p>Share RoomBuddy with someone looking for a home or rental service. When your referred customer completes their shifting, you earn ₹999.</p>
                        <div className="referral-flow"><span>Refer</span><ArrowRight /><span>They use RoomBuddy</span><ArrowRight /><span>Shifting completed</span><ArrowRight /><strong>You earn ₹999</strong></div>
                        <a className="button button-light" href="#download">Refer Through App <Gift /></a>
                    </div>
                </div>
            </section>

            <section className="section-pad owners-section" id="owners">
                <div className="two-column owners-layout">

                    <div className="content-copy owners-content">
                        <span className="section-kicker">Own a property?</span>
                        <h2>List Your Home for Free</h2>
                        <p>Property owners can list their available homes on RoomBuddy without a listing fee.</p>

                        <div className="owner-benefits">
                            {[
                                "Free property listing",
                                "Showcase property photos",
                                "Add property details",
                                "Reach RoomBuddy users",
                                "Connect with potential tenants"
                            ].map((item) => (
                                <span key={item}>
                                    <Check />
                                    {item}
                                </span>
                            ))}
                        </div>

                        <div className="button-row">
                            <a
                                className="button button-primary"
                                href="https://play.google.com/store/apps/details?id=com.roombuddy.app"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                List Your Property <ArrowRight />
                            </a>
                        </div>
                    </div>

                    <div className="image-frame owners-image">
                        <img
                            src={buddyimgtwo}
                            alt="RoomBuddy team helping residents move into a property"
                        />
                    </div>

                </div>
            </section>

            <section className="content-band why-section" >
                <div className="section-pad" style={{ paddingTop: 0 }}>
                    <div className="section-heading compact-heading"><span className="section-kicker">Why RoomBuddy?</span><h2>One Reliable Partner at Every Step</h2></div>
                    <div className="why-grid">
                        {benefits.map(([Icon, title, copy]) => <article className="glass-panel" key={title}><span className="icon-tile"><Icon /></span><h3>{title}</h3><p>{copy}</p></article>)}
                    </div>
                </div>
            </section>

            <section className="journey-banner">
                <div className="section-pad">
                    <span className="section-kicker section-kicker-light">One app. Complete rental journey.</span>
                    <h2>From Finding a Home to Moving In — RoomBuddy Has You Covered.</h2>
                    <div className="journey-words">{["Search", "Discover", "Contact", "Finalize", "Shift", "Settle In"].map((word, index) => <span key={word}>{word}{index < 5 && <ArrowRight />}</span>)}</div>
                    <p>All RoomBuddy services are available through the mobile app.</p>
                    <a className="button button-light" href="https://play.google.com/store/apps/details?id=com.roombuddy.app" target="_blank" style={{ marginTop: "1.5rem" }}>Download RoomBuddy App <Download /></a>
                </div>
            </section>

            <section className="section-pad download-section" id="download">
                <div className="download-panel glass-solid">
                    <div>
                        <span className="section-kicker">Get RoomBuddy</span>
                        <h2>Ready to Find Your Next Home?</h2>
                        <p>Download the RoomBuddy app and access rental, property search, shifting and referral services in one place.</p>
                        <a className="play-button" href="https://play.google.com/store/apps/details?id=com.roombuddy.app" target="_blank" aria-label="Download RoomBuddy on Google Play"><span className="play-icon">▶</span><span><small>GET IT ON</small><strong>Google Play</strong></span></a>
                    </div>

                    <div className="qr-wrap" aria-label="RoomBuddy app QR code area">
                        <img className="qr-placeholder" src={ScannerRoomBuddy} alt="" aria-hidden="true" />
                        <strong>Scan QR Code to Download</strong>
                        <small>RoomBuddy for Android</small>
                    </div>
                </div>
            </section>

            <footer className="footer">
                <div className="section-pad footer-grid">
                    <div><Brand /><p>From finding a home to moving in, your rental journey is easier with RoomBuddy.</p></div>
                    <div>
                        <h3>Services</h3>
                        <a href="#instant-assistant">Instant Assistant</a>
                        <a href="#room-search">Room Search</a>
                        <a href="#shifting">Shifting Services</a>
                        <a href="#refer">Refer & Earn</a>
                        <a href="#owners">Free Property Listing</a>
                    </div>
                    <div>
                        <h3>Company</h3>
                        <a href="/privacy">Privacy Policy</a>
                        <a href="/termsConditions">Terms & Conditions</a>
                    </div>
                    <div>
                        <h3>Contact</h3>
                        <a href="tel:+917989237393">📞 +91 7989237393</a>
                        <a href="mailto:support@roombuddy.in">✉️ support@roombuddy.in</a>
                        <a>📍 Madhapur, Hitech City, Hyderabad</a>
                    </div>
                </div>
                <div className="footer-bottom">© 2025 RoomBuddy. All rights reserved.</div>
            </footer>

            <NewWhatsAppButton />
        </main>
    );
}